import { useCallback } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Tooltip as TooltipPrimitive } from "@base-ui/react/tooltip";
import { FigmaTypography } from "@libs/zapi-ui/custom";
import { Button } from "@libs/zapi-ui/ds/components/button";
import { Spinner } from "@libs/zapi-ui/ds/components/spinner";
import { Tooltip, TooltipTrigger } from "@libs/zapi-ui/ds/components/tooltip";
import { ArrowClockwiseIcon, WarningIcon } from "@phosphor-icons/react";
import { cn } from "@/zapi-frontend/shared/lib/utils";
import { useOutlineChangeStatus } from "../hooks/useOutlineChangeStatus";
import { usePresentationChatStore } from "../hooks/usePresentationChat";
import { getTooltipConfig } from "../const/consts";

type TRefreshOutlineButtonProps = {
  outlineGeneratedAt: string | null;
  hasSlides: boolean;
  onRefresh: () => void;
  isRefreshing: boolean;
  disabled: boolean;
};

export function RefreshOutlineButton({
  outlineGeneratedAt,
  hasSlides,
  onRefresh,
  isRefreshing,
  disabled
}: TRefreshOutlineButtonProps) {
  const { t } = useTranslation("projectHome");
  const { t: tCommon } = useTranslation("common");
  const { id: projectId } = useParams() as { id: string };
  const status = useOutlineChangeStatus(outlineGeneratedAt, hasSlides);
  const hasChanges = status !== "up_to_date" && status !== "no_outline";
  const config = getTooltipConfig(t)[status];
  const isChatRefreshing = usePresentationChatStore((s) => !!s.loadingStates[projectId]?.loadingMessageId);

  const handleRefresh = useCallback(() => {
    onRefresh();
  }, [onRefresh]);

  const isDisabled = disabled || isChatRefreshing || (!hasChanges && !isRefreshing);

  return (
    <Tooltip>
      <TooltipTrigger render={<span className={cn(isDisabled && "cursor-not-allowed")} />}>
        <Button
          type="button"
          variant="outline"
          onClick={handleRefresh}
          disabled={isRefreshing || isDisabled}
          data-testid="presentation-refresh-outline"
          className={cn("min-w-[168px]", isDisabled && "pointer-events-none")}
        >
          {isRefreshing ? (
            <>
              <Spinner data-icon="inline-start" aria-label={tCommon("loading")} />
              {t("presentation.refreshing")}
            </>
          ) : (
            <>
              <ArrowClockwiseIcon size={16} />
              {t("presentation.refreshOutline")}
              {hasChanges && <WarningIcon size={16} />}
            </>
          )}
        </Button>
      </TooltipTrigger>
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Positioner side="bottom" sideOffset={8} align="end" className="isolate z-50">
          <TooltipPrimitive.Popup
            data-slot="hover-card-content"
            className="z-50 w-[255px] max-w-80 origin-(--transform-origin) rounded-lg bg-popover p-0 text-sm text-popover-foreground shadow-md ring-1 ring-foreground/10 outline-hidden duration-100 data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95"
          >
            <div className="flex gap-3.5 items-start pr-3.5 py-3">
              <div className={cn("w-[2px] min-h-[23px] self-stretch rounded-r-sm shrink-0", config.barColor)} />
              <div className="flex flex-col gap-1.5 py-0.5 flex-1 min-w-0">
                <FigmaTypography variant="label-2" color="zinc900">
                  {config.title}
                </FigmaTypography>
                <FigmaTypography variant="body-3" color="zinc600">
                  {config.description}
                </FigmaTypography>
              </div>
            </div>
          </TooltipPrimitive.Popup>
        </TooltipPrimitive.Positioner>
      </TooltipPrimitive.Portal>
    </Tooltip>
  );
}
